import { immerable } from 'immer';
import { v4 as uuidv4, validate } from 'uuid';
import type { ItemUserPodcastDTO } from '../dto/item-user.dto.js';
import type { PodcastDTO } from '../dto/podcast.dto.js';
import type { PodcastEpisodeDTO } from '../dto/podcast-episode.dto.js';
import { Channel } from './channel.model.js';
import { Item, type ItemVisibility } from './item.model.js';
import type { PodcastGenre } from './podcast-genres.js';

export class Podcast extends Channel implements PodcastDTO {
  override [immerable] = true;

  author: string;
  categories?: PodcastGenre[];
  config: {
    defaultItemsVisibility: ItemVisibility;
    hostedByFuuze: boolean;
    isPublished: boolean;
    usePodcastImageAsEpisodeImage: boolean;
  };
  override description: string;
  explicit: boolean;
  feedUrl: string;
  override image100: string;
  override image30: string;
  override image600: string;
  override image60: string;
  itunesTrackId?: number;
  ownerEmail?: string;
  summary?: string;

  constructor(data: PodcastDTO) {
    super(data);
    this.author = data.author;
    this.categories = data.categories;
    this.config = data.config as Podcast['config'];
    this.description = data.description;
    this.explicit = data.explicit;
    this.feedUrl = data.feedUrl;
    this.image100 = data.image100;
    this.image30 = data.image30;
    this.image60 = data.image60;
    this.image600 = data.image600;
    this.itunesTrackId = data.itunesTrackId;
    this.ownerEmail = data.ownerEmail;
    this.summary = data.summary;
  }

  static transformRawDataToPodcast(data: PodcastDTO) {
    return new Podcast(data);
  }

  static generateFakeForLoading() {
    return new Podcast({
      _id: uuidv4(),
      author: 'Loading...',
      config: {
        defaultItemsVisibility: 'public',
        hostedByFuuze: false,
        isPublished: false,
        usePodcastImageAsEpisodeImage: false,
      },
      description: 'Loading...',
      explicit: false,
      feedUrl: 'Loading...',
      image100: 'Loading...',
      image30: 'Loading...',
      image600: 'Loading...',
      image60: 'Loading...',
      slug: 'Loading...',
      tiers: [],
      title: 'Loading...',
    });
  }

  isFakeForLoading() {
    return validate(this._id);
  }

  isFuuze() {
    return this.slug === 'fuuze';
  }
}

export class PodcastEpisode extends Item implements PodcastEpisodeDTO {
  categories?: PodcastGenre[];
  channel: string;
  estimatedDuration: number;
  explicit: boolean;
  itemUser?: Pick<ItemUserPodcastDTO, 'listened' | 'position'> | null;
  inCreation?: boolean;
  nbOfSpotlights?: number;

  constructor(episode: PodcastEpisodeDTO) {
    super(episode);
    this.categories = episode.categories;
    this.channel = episode.channel as string;
    this.estimatedDuration = episode.estimatedDuration;
    this.explicit = episode.explicit;
    this.itemUser = episode.itemUser;
    this.inCreation = episode.inCreation;
    this.nbOfSpotlights = episode.nbOfSpotlights;
  }

  static transformRawDataToPodcastEpisode(data: PodcastEpisodeDTO) {
    return new PodcastEpisode(data);
  }

  static generateFakeForLoading() {
    return new PodcastEpisode({
      _id: uuidv4(),
      channel: uuidv4(),
      description: 'Loading...',
      estimatedDuration: 0,
      explicit: false,
      image100: 'Loading...',
      image30: 'Loading...',
      image600: 'Loading...',
      image60: 'Loading...',
      categories: [],
      slug: 'Loading...',
      title: 'Loading...',
      visibility: 'public',
    });
  }

  /**
   * Check if the episode is a placeholder for loading (if the _id is a valid UUID)
   */
  isFakeForLoading() {
    return validate(this._id);
  }
}
